import _ from 'lodash';
import { cellHover } from '../../redux/sagas/views/viewsActions';
import {
  Area,
  CellPosition,
  FilterResults,
  Freeze,
  GridEntity,
  SelectionCell,
  SelectionOperationType,
  Sheet,
  SheetSelection,
  SheetView,
  TemporarySelection,
} from '../../types';
import { expandSelectionArea } from '../selection/selection';
import { fitCellInViewport } from './viewport';
import {
  convertPositionToArea,
  convertPositionToSheetSelection,
  doAreasIntersect,
  getDefaultArea,
  isAreaInsideArea,
} from '../area/area';
import { DataDirection } from '../../../core/types/ui/position';
import { unionAreaWithMerges } from './merges';
import { getNextRowInSelection } from './jumpWithKeys';
import { getUnHiddenPosition, getViewportStartEnd } from './virtualViewport';

// #region handle stop selection

export const handleSelectionOperations = (
  activeSelection: SheetSelection,
  temporarySelection: TemporarySelection,
  position: CellPosition,
  maxRowIndex: number,
  maxColIndex: number
) => {
  const { op } = temporarySelection;
  switch (op) {
    case SelectionOperationType.Extend:
      return extendSelection(activeSelection.area, position);
    case SelectionOperationType.SelectionMove:
      const area = moveSelection(
        activeSelection.area,
        temporarySelection.initialPosition,
        position,
        maxRowIndex,
        maxColIndex
      );
      return {
        area,
        position,
      };
    case SelectionOperationType.ColMove:
    case SelectionOperationType.RowMove:
      return {
        position: position,
        area: moveRowColSelection(
          op,
          activeSelection.area,
          temporarySelection.initialPositionIndex,
          position,
          maxRowIndex,
          maxColIndex
        ),
      };
  }

  // return {
  //   area: activeSelection.area,
  //   position,
  // };
};

export const handleMouseHoverSelection = (
  sheet: Sheet,
  sheetView: SheetView,
  payload: ReturnType<typeof cellHover>['payload']
) => {
  const { ctrlKey, focusCellInViewport, cell, windowHeight, windowWidth } =
    payload;

  let position = payload.position;

  let selections: SheetSelection[] = _.cloneDeep(sheetView.selections);

  const selection =
    _.last(selections) ??
    convertPositionToSheetSelection(position, SelectionCell.Cell);

  let temporarySelection = _.cloneDeep(sheetView.temporarySelection);

  if (temporarySelection) {
    const updatedPos = handleSelectionOperations(
      selection,
      temporarySelection,
      position,
      sheet.maxRowIndex,
      sheet.maxColIndex
    );

    position = updatedPos.position;
    // selection.area = updatedPos.area;
    temporarySelection.area = updatedPos.area;
  } else {
    selection.area = expandSelectionArea(
      sheetView.activeCell,
      position,
      // sheet.maxRowIndex,
      // sheet.maxColIndex,
      selection.cell,
      sheet
    );

    if (ctrlKey) {
      const index = selections.length > 0 ? selections.length - 1 : 0;
      _.set(selections, index, selection);
    } else {
      selections = [selection];
    }
  }

  let allViewports = undefined;
  if (focusCellInViewport && windowWidth && windowHeight) {
    // Get ultimate last row and col for comparison
    const { ci: lastCol, ri: lastRow } = getNextVisibleRowCol(
      sheet,
      sheet.maxColIndex,
      sheet.maxRowIndex,
      sheetView.filterResults,
      0
    );

    const { start: rs, end: re } = getViewportStartEnd(
      sheetView.rowViewport.nonFrozenIndexes
    );
    const { start: cs, end: ce } = getViewportStartEnd(
      sheetView.colViewport.nonFrozenIndexes
    );
    // const rs = sheetView.rowViewport.nonFrozenIndexes[0];
    // const re = _.last(sheetView.rowViewport.nonFrozenIndexes) ?? rs;
    // const cs = sheetView.colViewport.nonFrozenIndexes[0];
    // const ce = _.last(sheetView.colViewport.nonFrozenIndexes) ?? cs;
    // const sheetViewport = sheetView.viewport;
    allViewports = fitCellInViewport(
      sheet,
      position,
      sheetView,
      windowWidth,
      windowHeight,
      {
        ignoreRow:
          selection.cell === SelectionCell.Column ||
          !(position.ri > re || position.ri < rs || position.ri === lastRow),
        ignoreCol:
          selection.cell === SelectionCell.Row ||
          !(position.ci > ce || position.ci < cs || position.ci === lastCol),
      }
    );
  }

  return {
    ...allViewports,
    selections,
    temporarySelection,
  };
};

// export const doMergeIntersectActiveArea = (merges: Area[], area: Area) => {
//   for (let merge of merges) {
//     if (doAreasIntersect(area, merge)) {
//       return merge;
//       // selection.area = expandSelectionToArea(merge, selection.area);
//       // activeCellArea = merge;
//       // break;
//     }
//   }

//   return;
// };

export const getNextVisibleRowCol = (
  sheet: Sheet,
  colIndex: number,
  rowIndex: number,
  filterResults?: FilterResults,
  skipIndexes?: number
) => {
  const row = getUnHiddenPosition(
    sheet.rowSizes,
    sheet.hiddenRows,
    rowIndex,
    sheet.maxRowIndex,
    DataDirection.Forward,
    sheet.freezedRows,
    filterResults,
    skipIndexes
  );
  const col = getUnHiddenPosition(
    sheet.colSizes,
    sheet.hiddenCols,
    colIndex,
    sheet.maxColIndex,
    DataDirection.Forward,
    sheet.freezedCols,
    undefined,
    skipIndexes
  );

  return {
    ci: col,
    ri: row,
  };
};

// function moveFirstSelectionToLast(array: SheetSelection[]) {
//   const firstElement = array.shift();
//   if (firstElement) array.push(firstElement);
// }

export const jumpActiveCellWithinSelections = (
  sheet: Sheet,
  sheetView: SheetView,
  // selections: SheetSelection[],
  // activeCell: CellPosition,
  windowWidth: number,
  windowHeight: number
): Partial<SheetView> => {
  const selections = sheetView.selections;
  let activeCell = sheetView.activeCell;
  // Active Cell will always be part of last selection in selections

  let newSelections = _.cloneDeep(selections);
  let newActiveCell = { ...activeCell };

  const area = _.last(newSelections)?.area ?? getDefaultArea();

  getNextRowInSelection(sheet, sheetView, newActiveCell, area, newSelections);

  return {
    activeCell: newActiveCell,
    selections: newSelections,
    activeCellArea: unionAreaWithMerges(
      sheet.merges,
      convertPositionToArea(newActiveCell)
    ),
    ...fitCellInViewport(
      sheet,
      newActiveCell,
      sheetView,
      windowWidth,
      windowHeight
    ),
  };
};

const extendSelection = (originalArea: Area, position: CellPosition) => {
  const { start, end } = originalArea;
  const { ci, ri } = position;

  const rowDiff = calculatePositionDifference(start.ri, end.ri, ri);
  const colDiff = calculatePositionDifference(start.ci, end.ci, ci);

  const area = _.cloneDeep(originalArea);

  if (rowDiff >= colDiff) {
    position.ci = end.ci;
    area.start.ri = Math.min(start.ri, ri);
    area.end.ri = Math.max(end.ri, ri);
  } else {
    position.ri = end.ri;
    area.start.ci = Math.min(start.ci, ci);
    area.end.ci = Math.max(end.ci, ci);
  }

  return {
    area,
    position,
  };
};

const moveSelection = (
  originalArea: Area,
  initialPosition: CellPosition,
  newPosition: CellPosition,
  maxRowIndex: number,
  maxColIndex: number
) => {
  const { start } = originalArea;
  const colDiff = initialPosition.ci - start.ci;
  const rowDiff = initialPosition.ri - start.ri;

  const totalRows = originalArea.end.ri - originalArea.start.ri;
  const totalCols = originalArea.end.ci - originalArea.start.ci;

  const rowStart = adjustMoveIndex(
    newPosition.ri,
    rowDiff,
    totalRows,
    maxRowIndex
  );

  const colStart = adjustMoveIndex(
    newPosition.ci,
    colDiff,
    totalCols,
    maxColIndex
  );

  const rowEnd = rowStart + totalRows;
  const colEnd = colStart + totalCols;

  return {
    start: { ri: rowStart, ci: colStart },
    end: { ri: rowEnd, ci: colEnd },
  };
};

const moveRowColSelection = (
  op: SelectionOperationType.RowMove | SelectionOperationType.ColMove,
  originalArea: Area,
  intialPositionIndex: number,
  newPosition: CellPosition,
  maxRowIndex: number,
  maxColIndex: number
) => {
  const alias = op === SelectionOperationType.ColMove ? 'ci' : 'ri';
  const max = op === SelectionOperationType.ColMove ? maxColIndex : maxRowIndex;

  const { start } = originalArea;
  const diff = intialPositionIndex - start[alias];
  // const rowDiff = intialPositionIndex - start.ri;

  const totalEntities = originalArea.end[alias] - originalArea.start[alias];
  // const totalCols = originalArea.end.ci - originalArea.start.ci;

  const newStart = adjustMoveIndex(
    newPosition[alias],
    diff,
    totalEntities,
    max
  );

  const newEnd = newStart + totalEntities;

  // const colStart = adjustMoveIndex(
  //   newPosition.ci,
  //   colDiff,
  //   totalCols,
  //   maxColIndex
  // );

  // const rowEnd = rowStart + totalRows;
  // const colEnd = colStart + totalCols;

  return {
    start: {
      ...originalArea.start,
      [alias]: newStart,
    },
    end: {
      ...originalArea.end,
      [alias]: newEnd,
    },
  };
};

const calculatePositionDifference = (
  areaStart: number,
  areaEnd: number,
  position: number
) => {
  if (position < areaStart) {
    return areaStart - position;
  } else if (position > areaEnd) {
    return position - areaEnd;
  }

  return 0;
};

const adjustMoveIndex = (
  index: number,
  diff: number,
  total: number,
  maxIndex: number
) => Math.max(0, Math.min(index - diff, maxIndex - total));

// export const generateAliasesFromEntity = (
//   entity: GridEntity
// ) => {
//   const isColumn = entity === GridEntity.Column

//   const
// }
